import { Story } from "../../types/Story";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import Color from "color";
import { RGBColor, closest as closestColor } from "color-diff";
import RenderWhenInView from "../../functional-components/RenderWhenInView";
import ImageOrVideo from "../ImageOrVideo";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import classNames from "classnames";
import { storiesBaseUrl } from "../../config/urls";
import { unwrapAsset } from "../../utils/CockpitUtils";
import COLORS from "../../config/colors";
import "./style.scss";
import Icon from "../Icon";

function hex2RGB(c: string) {
  const color = Color(c, "hex").object();
  return { R: color.r, G: color.g, B: color.b };
}

function RGB2hex(c: RGBColor) {
  const rgb = { r: c.R, g: c.G, b: c.B };
  return Color(rgb, "rgb").hex();
}

const textColors = {
  bjelin_middlegrey: "#787472",
  dark_sand: "#877054",
  dark_brown: "#7d634e",
  dark_red: "#8f3736",
  green: "#656d35",
  dark_blue: "#2f739d"
};

export const typeIcons: { [index: string]: string } = {
  article: "book",
  case: "clipboard-list"
};

const palette = Object.values(textColors).map(c => hex2RGB(c));

const StoryPreview = ({ story }: { story: Story }) => {
  const t = useBjelinTranslations();
  const images = story.images?.map(item =>
    item.value ? unwrapAsset(item.value) : item
  );
  const bkgColor = Color(story.featured_image.colors?.[0])
    .darken(0.5)
    .saturate(0.5);

  let truncatedDescription = story.description;
  if (truncatedDescription && truncatedDescription.length > 200) {
    truncatedDescription = truncatedDescription.substring(0, 200) + "…";
  }

  let truncatedTitle = story.title;
  if (truncatedTitle && truncatedTitle.length > 100) {
    truncatedTitle = truncatedTitle.substring(0, 100) + "…";
  }

  return (
    <motion.div
      style={{
        display: "block",
        position: "relative"
      }}
      className={classNames("story-preview", {
        featured: story.featured
      })}
      initial={{ opacity: 0 }}
      exit={{
        opacity: 0,
        x: "200%",
        transition: {
          type: "spring",
          duration: 1
        }
      }}
      animate={{ opacity: 1 }}
      transition={{
        type: "spring",
        damping: 50,
        stiffness: 400,
        mass: 1
      }}
      role="listitem"
    >
      <Link to={storiesBaseUrl + "/" + story.path}>
        <div
          className={classNames(
            "preview-container",
            story.featured ? "preview-text-on-image" : "preview-side-by-side",
            { "long-title": story.title.length > 50 }
          )}
          style={{
            color: story.featured
              ? "#fff"
              : story.featured_image.colors?.[2]
              ? RGB2hex(
                  closestColor(
                    hex2RGB(story.featured_image.colors?.[2]),
                    palette
                  )
                )
              : COLORS.bjelin_dark_grey,
            background: !story.featured
              ? "transparent"
              : bkgColor
              ? bkgColor.hex()
              : COLORS.bjelin_middlegrey
          }}
        >
          <div className="preview-image">
            <RenderWhenInView>
              <ImageOrVideo
                image={
                  story.featured_image ? story.featured_image : images?.[0]
                }
                centerImage
              />
            </RenderWhenInView>
            {/*<div className={"types"}>
              {Array.isArray(story.type) &&
                story.type.map(type => (
                  <span
                    className="tag-type"
                    key={type}
                    title={t("story-type." + type)}
                  >
                    {typeIcons[type] && (
                      <>
                        <Icon name={typeIcons[type]} />{" "}
                      </>
                    )}
                  </span>
                ))}
            </div>*/}
          </div>
          <div className="preview-content">
            <p className="tags">
              {story.subject && (
                <span>{t("story.subject." + story.subject)}</span>
              )}
              {story.category && (
                <span>{t("story.category." + story.category)}</span>
              )}
              {story.location && <span>{story.location}</span>}
            </p>
            <h4>{truncatedTitle}</h4>
            <p className="content-summary">{truncatedDescription}</p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default StoryPreview;
